// web-v2/src/lib/sign-in.js
//
// THE LOGIN PAGE'S DECISIONS (personal-logins L5, PLAN 8 and R2, DESIGN-REVIEW M9, M10, m13). Plain
// ESM with no imports, so tests/login-methods-skew.test.js and tests/gis-redirect-fallback.test.js
// drive it under node. routes/login.jsx renders what these answer and decides nothing itself.
//
//   loadMethods   GET /api/tracon/auth/methods with its own timeout and no toast. Anything but a
//                 200 carrying the frozen R2 shape is { ok:false } (a bundle can be live while
//                 Render's deploy aborted, so the door may 404).
//   loginView     the password form shows UNLESS methods answered 200 with shared_login === false;
//                 the Google button shows only on a 200 carrying a client id AND listing this origin
//                 (the Render-served /v2 is not a registered Google origin, m13).
//   surfaceOf     'standalone' for the installed PWA, else 'browser' (recorded on the login, M10).
//   gisOptions    popup in a browser; GIS redirect mode (login_uri = the API callback) in the PWA
//                 and whenever the broker says the popup did not open.
//   readReturnFragment  the callback's #t=<token>&s=<nonce> / #e=<code>.
//   newNonce / markRedirectPending / takeRedirectPending / acceptReturn  a returned token counts only
//                 with the nonce of the redirect this tab started (login CSRF, review L5-SEC-1).
//   signInWords   plain words for every sign-in refusal code; the server's `error` is never shown
//                 (every new 401 carries the legacy "Invalid or expired token", R1).

export const METHODS_PATH = '/api/tracon/auth/methods';
export const GOOGLE_PATH = '/api/tracon/auth/google';
export const PASSWORD_PATH = '/api/tracon/auth';
export const CALLBACK_PATH = '/api/tracon/auth/google/callback';
export const GIS_SRC = 'https://accounts.google.com/gsi/client';
export const METHODS_TIMEOUT_MS = 8000;
export const RETURN_KEY = 'tracon_login_return';
// The chooser hint only (review L5-UX-10): the server's hd and email-domain check is the gate.
export const AGENCY_HD = 'ndemnifly.com';

const SURFACES = ['browser', 'standalone'];

async function getJson(fetchImpl, path, { method = 'GET', body, timeoutMs }) {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  let timer;
  const timeout = new Promise((_, reject) => { timer = setTimeout(() => { if (controller) controller.abort(); reject(new Error('timeout')); }, timeoutMs); });
  try {
    const res = await Promise.race([fetchImpl(path, {
      method, cache: 'no-store', headers: { 'Content-Type': 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {}), ...(controller ? { signal: controller.signal } : {}),
    }), timeout]);
    let data = null;
    try { data = await res.json(); } catch { data = null; }
    return { status: res.status, data };
  } finally { clearTimeout(timer); }
}

/** The methods door, or { ok:false, reason } for any answer that is not the frozen shape. Never throws. */
export async function loadMethods({ fetchImpl = globalThis.fetch, timeoutMs = METHODS_TIMEOUT_MS } = {}) {
  let r;
  try { r = await getJson(fetchImpl, METHODS_PATH, { timeoutMs }); } catch (e) { return { ok: false, reason: e && e.message === 'timeout' ? 'timeout' : 'network' }; }
  if (r.status !== 200) return { ok: false, reason: `http_${r.status}` };
  const d = r.data;
  if (!d || d.success !== true || typeof d.shared_login !== 'boolean' || !d.google || typeof d.google !== 'object') return { ok: false, reason: 'malformed' };
  const clientId = typeof d.google.client_id === 'string' && d.google.client_id.trim() ? d.google.client_id.trim() : null;
  const origins = Array.isArray(d.google.origins) ? d.google.origins.filter((o) => typeof o === 'string') : [];
  return { ok: true, shared_login: d.shared_login, google: { client_id: clientId, origins } };
}

/** What the login page offers. `why` names an empty page. */
export function loginView(methods, origin) {
  const ok = !!(methods && methods.ok);
  const password = !(ok && methods.shared_login === false);
  const clientId = ok && methods.google ? methods.google.client_id : null;
  const google = !!clientId && methods.google.origins.includes(origin);
  const why = password || google ? null : (clientId ? 'origin_not_registered' : 'no_method');
  return { password, google, clientId: google ? clientId : null, why };
}

/** 'standalone' when TRACON runs as the installed app, else 'browser'. */
export function surfaceOf(win = globalThis.window) {
  try {
    if (win && typeof win.matchMedia === 'function' && win.matchMedia('(display-mode: standalone)').matches) return 'standalone';
    if (win && win.navigator && win.navigator.standalone === true) return 'standalone';
  } catch { /* a browser without matchMedia is a browser */ }
  return 'browser';
}

/** The GIS initialize options. Redirect mode posts the credential to the API callback, which answers 302 /login#t= or #e=. */
export function gisOptions({ clientId, origin, surface, popupFailed = false, callback, nonce }) {
  const where = SURFACES.includes(surface) ? surface : 'browser';
  const base = { client_id: clientId, auto_select: false, cancel_on_tap_outside: true, context: 'signin', hd: AGENCY_HD };
  if (where === 'standalone') return { ...base, ux_mode: 'redirect', login_uri: `${origin}${CALLBACK_PATH}`, nonce };
  if (popupFailed) return { ...base, ux_mode: 'redirect', login_uri: `${origin}${CALLBACK_PATH}?surface=browser`, nonce };
  return { ...base, ux_mode: 'popup', callback };
}

/** The callback's answer in the URL fragment: { token } or { code }, else null. */
export function readReturnFragment(hash) {
  const h = String(hash || '').replace(/^#/, '');
  if (!h) return null;
  const params = new URLSearchParams(h);
  const t = params.get('t');
  if (t && /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(t)) {
    const n = params.get('s');
    return n && /^[0-9a-f]{32}$/.test(n) ? { token: t, s: n } : { token: t };
  }
  const e = params.get('e');
  if (e) return { code: /^[a-z_]{1,60}$/.test(e) ? e : 'sign_in_failed' };
  return null;
}

// LOGIN CSRF (review L5-SEC-1, and the security review on its first fix): the callback's #t= is
// honoured only when its s is the nonce of the redirect sign-in THIS tab started. A crafted
// /login#t=<someone else's token> would otherwise replace the broker's own token with no prompt,
// and every later write would be recorded under that other person. The page initializes GIS with a
// fresh nonce; the button's click (never the render) stores it as a one-time marker that expires;
// Google signs it into the credential and the callback echoes it from the VERIFIED token as s.
// sessionStorage is per tab, so a link opened elsewhere has no marker, and a link landing while a
// sign-in is pending carries no s or another sign-in's.
export const PENDING_KEY = 'tracon_login_pending';
export const PENDING_TTL_MS = 5 * 60_000;
const NONCE_SHAPE = /^[0-9a-f]{32}$/;
/** A fresh 32-hex nonce for one redirect sign-in. */
export function newNonce() {
  const b = new Uint8Array(16);
  globalThis.crypto.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}
/** Writes this tab's one-time "this sign-in started here" marker. false when the nonce is malformed or storage is blocked. */
export function markRedirectPending(storage, { nonce, now = Date.now() } = {}) {
  if (typeof nonce !== 'string' || !NONCE_SHAPE.test(nonce)) return false;
  try { storage.setItem(PENDING_KEY, JSON.stringify({ v: nonce, at: now })); return true; } catch { return false; }
}
/** Takes (removes) the marker; its nonce only when well-formed and unexpired, else null. Never throws. */
export function takeRedirectPending(storage, { now = Date.now() } = {}) {
  let raw;
  try { raw = storage.getItem(PENDING_KEY); storage.removeItem(PENDING_KEY); } catch { return null; }
  try {
    const m = JSON.parse(raw);
    return m && typeof m.v === 'string' && NONCE_SHAPE.test(m.v) && Number.isFinite(m.at) && now >= m.at && now - m.at <= PENDING_TTL_MS ? m.v : null;
  } catch { return null; }
}
/** What the page does with a returned fragment: a token only when its s is this tab's pending nonce; a code only after a start here. */
export function acceptReturn(back, pendingNonce) {
  if (!back) return null;
  if (!pendingNonce) return { code: 'not_started_here' };
  if (back.token) return back.s === pendingNonce ? { token: back.token } : { code: 'not_started_here' };
  return back;
}

/** POST the GIS credential. { ok, token, user } or { ok:false, code, status }. Never throws. */
export async function postGoogleCredential({ credential, surface, fetchImpl = globalThis.fetch, timeoutMs = 30_000 }) {
  let r;
  try { r = await getJson(fetchImpl, GOOGLE_PATH, { method: 'POST', body: { credential, surface: SURFACES.includes(surface) ? surface : 'browser' }, timeoutMs }); }
  catch (e) { return { ok: false, status: 0, code: e && e.message === 'timeout' ? 'timeout' : 'network' }; }
  const d = r.data || {};
  if (r.status === 200 && d.success === true && typeof d.token === 'string' && d.token) return { ok: true, token: d.token, user: d.user || null };
  return { ok: false, status: r.status, code: typeof d.code === 'string' ? d.code : (r.status === 429 ? 'too_many_attempts' : `http_${r.status}`) };
}

/** Loads the GIS script once (lazily, only when the button will show). */
let gisPromise = null;
export function loadGisScript(doc = globalThis.document, { timeoutMs = 15_000 } = {}) {
  if (gisPromise) return gisPromise;
  gisPromise = new Promise((resolve, reject) => {
    const s = doc.createElement('script');
    s.src = GIS_SRC; s.async = true; s.defer = true;
    const timer = setTimeout(() => reject(new Error('gis_load_failed')), timeoutMs);
    s.onload = () => { clearTimeout(timer); resolve(); };
    s.onerror = () => { clearTimeout(timer); reject(new Error('gis_load_failed')); };
    doc.head.appendChild(s);
  }).catch((e) => { gisPromise = null; throw e; });
  return gisPromise;
}
export function _resetGis() { gisPromise = null; }

// Every code the sign-in doors answer (PLAN R2), plus the page's own. A code with no entry gets FALLBACK.
export const SIGN_IN_WORDS = Object.freeze({
  invalid_credential: 'Google could not confirm that sign-in. Try again.',
  hd_mismatch: 'That Google account is not an ndemnifly.com account. Sign in with your agency account.',
  email_unverified: 'Google has not verified that email address yet.',
  user_unknown: 'That account is not a TRACON user yet. Ask David to add you.',
  user_disabled: 'That TRACON user is switched off. Ask David.',
  sub_mismatch: 'That address is tied to a different Google account. David can reset it on the users page.',
  demo_not_here: 'That sign-in belongs to the TRACON demo, not the live system.',
  personal_login_unconfigured: 'Google sign-in is not set up on the server yet.',
  identity_unavailable: 'The sign-in check is unavailable right now. Try again shortly.',
  too_many_attempts: 'Too many sign-in attempts. Try again in 15 minutes.',
  session_revoked: 'Your session was ended. Sign in again.',
  shared_login_off: 'The shared login is off. Sign in with Google.',
  csrf_mismatch: 'The browser did not keep the sign-in cookie. Try again, or use the Google button in a normal browser window.',
  owner_only: 'That needs the owner\'s own Google sign-in.',
  gis_load_failed: 'The Google sign-in button could not load. Check the connection, or try another browser.',
  timeout: 'The server took too long to answer. Try again.',
  network: 'TRACON could not reach the server. Check the connection.',
  bad_password: 'Invalid username or password.',
  no_method: 'Sign-in is not available right now. Try again shortly.',
  origin_not_registered: 'Google sign-in works only at tracon.ndemnifly.com. Open TRACON there.',
  not_started_here: 'That sign-in link was not started in this window, so it was not used. Sign in below.',
});
export const FALLBACK_WORDS = 'Sign-in failed. Try again.';
// Shown once the broker switches to the full-page sign-in, so the switch is visible (review L5-UX-7).
export const FULL_PAGE_NOTE = 'Full-page sign-in is on. Press the Google button again; it opens Google in this tab.';
export function signInWords(code) { return Object.prototype.hasOwnProperty.call(SIGN_IN_WORDS, code) ? SIGN_IN_WORDS[code] : FALLBACK_WORDS; }

/** The password door's answer in words (today's 401 kept; R2's 410 and 503; 429). */
export function passwordWords(status, code) {
  if (code && Object.prototype.hasOwnProperty.call(SIGN_IN_WORDS, code)) return SIGN_IN_WORDS[code];
  if (status === 401) return SIGN_IN_WORDS.bad_password;
  if (status === 410) return SIGN_IN_WORDS.shared_login_off;
  if (status === 429) return SIGN_IN_WORDS.too_many_attempts;
  if (status === 503) return SIGN_IN_WORDS.identity_unavailable;
  return FALLBACK_WORDS;
}
